import React, { useState, useMemo } from 'react';
import {
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    Box
} from '@material-ui/core';
import Papa from 'papaparse';

const columnOptions = [
    { key: 'date', label: 'Date' },
    { key: 'type', label: 'Type' },
    { key: 'description', label: 'Description' },
    { key: 'moneyIn', label: 'Money In' },
    { key: 'moneyOut', label: 'Money Out' },
    { key: 'accountCode', label: 'Account Code' },
    { key: 'accountName', label: 'Account Name' },
    { key: 'taxAmount', label: 'Tax Amount' },
    { key: 'taxCode', label: 'Tax Code' },
    { key: 'contact', label: 'Contact' },
    { key: 'classification', label: 'Classification' }
];

function arrayToMap(arr) {
    return new Map(arr.map(item => [item.value, item.label]));
}

const ExportTransactions = ({ onClose, transactions = [], contacts = [], classification = [], chart = [] }) => {
    // Create lookup maps for contacts and classificatio
    const { contactsMap, classificationMap, chartMap } = useMemo(() => ({
        contactsMap: arrayToMap(contacts),
        classificationMap: arrayToMap(classification),
        chartMap: arrayToMap(chart),
    }), [contacts, classification, chart]);

    const [selectedColumns, setSelectedColumns] = useState({
        date: true,
        type: true,
        description: true,
        moneyIn: true,
        moneyOut: true,
        accountCode: true,
        accountName: false,
        taxAmount: false,
        taxCode: false,
        contact: false,
        classification: false
    });

    const handleColumnChange = (columnKey) => {
        setSelectedColumns(prev => ({
            ...prev,
            [columnKey]: !prev[columnKey]
        }));
    };

    const formatTransactionData = (transaction) => {
        const data = {};
        
        if (selectedColumns.date) data['Date'] = transaction.TransactionDate;
        if (selectedColumns.type) data['Type'] = transaction.TransactionType;
        if (selectedColumns.description) data['Description'] = transaction.Description;
        if (selectedColumns.moneyIn) data['Money In'] = transaction.Amount > 0 ? transaction.Amount : '';
        if (selectedColumns.moneyOut) data['Money Out'] = transaction.Amount < 0 ? Math.abs(transaction.Amount) : '';
        if (selectedColumns.taxAmount) data['Tax'] = transaction.gstAmount ? Math.abs(transaction.gstAmount).toFixed(2) : ''; 
        if (selectedColumns.classification) {
            const classificationLabel = classificationMap.get(transaction.classificationId);
            data['Classification'] = classificationLabel || '';
        }
        if (transaction.Coding[0]) {
            if (selectedColumns.accountCode) data['Account Code'] = transaction.Coding[0].AccountCode !== '-1' ? transaction.Coding[0].AccountCode : '';
            if (selectedColumns.accountName) {
                const accountLabel = chartMap.get(transaction.Coding[0].AccountCode);
                data['Account Name'] = accountLabel || '';
            }
            if (selectedColumns.taxCode) data['Tax Code'] = transaction.Coding[0].GstCode || '';
            if (selectedColumns.contact) {
                const contactLabel = contactsMap.get(transaction.Coding[0].contactId);
                data['Contact'] = contactLabel || '';
            } 
        } else {
            if (selectedColumns.accountCode) data['Account Code'] = '';
            if (selectedColumns.accountName) data['Account Name'] = '';
            if (selectedColumns.taxCode) data['Tax Code'] = '';
            if (selectedColumns.contact) data['Contact'] = '';
            
        }

        return data;
    };

    const handleExport = () => {
        if (transactions.length === 0) return;

        const csvData = transactions.map(formatTransactionData);
        const csv = Papa.unparse(csvData);
        
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `transactions_export_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        onClose();
    };

    const hasSelectedColumns = Object.values(selectedColumns).some(selected => selected);

    if (transactions.length === 0) {
        return (
            <Box style={{ padding: '40px', textAlign: 'center' }}>
                <Typography variant="body1" color="textSecondary">
                    No selected transactions to export
                </Typography>
                <Box style={{ marginTop: '20px' }}>
                    <Button onClick={onClose} color="primary">
                        Close
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box style={{ padding: '20px 40px' }}>
            <Typography variant="h5" gutterBottom style={{ borderBottom: '1px solid #e0e0e0', paddingBottom: '10px' }}>
                {transactions.length} transaction{transactions.length !== 1 ? 's' : ''} selected for export
            </Typography>
            
            <Typography variant="h6" style={{ marginTop: '20px', marginBottom: '10px' }}>
                Select columns to export:
            </Typography>
            
            <Box display="flex" flexDirection="column">
                {columnOptions.map(option => (
                    <FormControlLabel
                        key={option.key}
                        style={{ width: '200px' }}
                        control={
                            <Checkbox
                                checked={selectedColumns[option.key]}
                                onChange={() => handleColumnChange(option.key)}
                                color="primary"
                            />
                        }
                        label={option.label}
                    />
                ))}
            </Box>

            <div style={{ marginTop: 16, maxWidth: 350, paddingBottom: 10, fontSize: 13, color: '#888' }}>
                <strong style={{ color: '#444' }}>Important:</strong> If you export transactions and then later upload the same file, this may create duplicate records. <a href="https://help.reckon.com/article/xubasickql-fast-coding" target="_blank" rel="noopener noreferrer" style={{ color: '#0b6d87ff' }}>Learn more about in fast coding</a>
            </div>

            <Box style={{ marginTop: '20px', paddingTop: '15px', display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #e0e0e0' }}>
                <Button onClick={onClose} color="primary">
                    Cancel
                </Button>
                <Button 
                    onClick={handleExport} 
                    color="primary" 
                    variant="contained"
                    disabled={!hasSelectedColumns}
                >
                    Export CSV
                </Button>
            </Box>
        </Box>
    );
};

export default ExportTransactions;


// WEBPACK FOOTER //
// ./src/components/Transaction/ExportTransactions.js